import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import Select from "react-select";
import axios from "axios";
import { debounce } from "lodash";
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { BASE_URL, AUTO_GENERATED_PLACEHOLDER } from "../../config";
import PlanBadge from "../PlanBadge";

const genderOptions = [
  { value: "m", label: "Male" },
  { value: "f", label: "Female" },
];

const civilStatusOptions = [
  { value: "s", label: "Single" },
  { value: "m", label: "Married" },
  { value: "p", label: "Separated" },
  { value: "w", label: "Widowed" },
  { value: "d", label: "Divorced" },
];

const getSelectedOption = (options, value, aliases = {}) => {
  const normalizedValue = String(value ?? "").trim().toLowerCase();
  const optionValue = aliases[normalizedValue] ?? normalizedValue;

  return options.find((option) => option.value === optionValue) ?? null;
};

const isBlankOptionalValue = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "" || normalized === "null" || normalized === "undefined";
};

const normalizeOptionalDateForInput = (value) => {
  const normalized = String(value ?? "").trim();
  if (isBlankOptionalValue(normalized) || normalized.toLowerCase() === "invalid date") {
    return "";
  }

  const dateMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})/);
  if (!dateMatch) {
    return "";
  }

  const dateValue = dateMatch[1];
  return dateValue === "0000-00-00" ? "" : dateValue;
};

const normalizeOptionalDateForPayload = (value) => {
  const normalized = normalizeOptionalDateForInput(value);
  return normalized === "" ? null : normalized;
};

const sanitizeAgeInput = (value) => String(value ?? "").replace(/\D/g, "").slice(0, 3);

const normalizeOptionalAgeForPayload = (value) => {
  const normalized = String(value ?? "").trim();
  return isBlankOptionalValue(normalized) ? null : normalized;
};

const validateOptionalAge = (value, label) => {
  const normalized = String(value ?? "").trim();
  if (isBlankOptionalValue(normalized)) {
    return null;
  }

  if (!/^\d{1,3}$/.test(normalized)) {
    return `${label} must be a whole number from 0 to 999.`;
  }

  const age = Number(normalized);
  return age >= 0 && age <= 999 ? null : `${label} must be a whole number from 0 to 999.`;
};

const EditMemberForm = () => {
  const navigate = useNavigate();
  const { memberId } = useParams();
  const [activeTab, setActiveTab] = useState("basic");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState({
    region: false,
    province: false,
    towncity: false,
    barangay: false,
  });
  const [formData, setFormData] = useState({
    corp_code: "",
    plan_code: "",
    employee_id: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    suffix: "",
    vip_tag: false,
    nickname: "",
    date_of_birth: "",
    passport_no: "",
    policy_no: "",
    bank_account_no: "",
    bank_acct_name: "",
    gender: "",
    civil_status: "",
    nationality: "",
    streetno_bldg: "",
    region: "",
    province: "",
    towncity: "",
    barangay: "",
    postalcode: "",
    email_add: "",
    tel_no: "",
    mob_tel: "",
    sss_no: "",
    philhealth_required: false,
    philhealth_id: "",
    tin_no: "",
    position: "",
    designation: "",
    employment_date: "",
    department: "",
    member_type: "p",
    principal_id: "",
    hired_date: "",
    enrollment_date: "",
    activation_date: "",
    termination_date: "",
    suspension_date: "",
    reactivation_date: "",
    expiry_date: "",
    status_flag: "1",
    special_instructions: "",
    acceptance_age: "",
    termination_age: "",
    newfile: "",
    replacefile: "",
    reprintfile: "",
    bank_id: "",
    bank_branch: "",
    member_id: "",
    member_username: "",
    member_password: "",
  });
  const [corporates, setCorporates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [banks, setBanks] = useState([]);
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [townCities, setTownCities] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [principals, setPrincipals] = useState([]);
  const [errors, setErrors] = useState({});
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showAgeInfoModal, setShowAgeInfoModal] = useState(false);
  const [updatedMemberDetails, setUpdatedMemberDetails] = useState(null);
  const initialFormData = useRef(formData);

  const getSelectedBankOption = (value) => {
    const normalized = String(value ?? "").trim();
    return banks.find((bank) => String(bank.value) === normalized) ?? null;
  };

  const normalizeOptionalBankIdForPayload = (value) => {
    const normalized = String(value ?? "").trim();
    if (isBlankOptionalValue(normalized) || /^0+$/.test(normalized)) {
      return null;
    }

    return getSelectedBankOption(normalized)?.value ?? null;
  };

  // Fetch member data
  useEffect(() => {
    const fetchMember = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${BASE_URL}/members/${memberId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        });
        const member = response.data.member;
        setFormData({
          ...formData,
          ...member,
          date_of_birth: member.date_of_birth ? member.date_of_birth.split(' ')[0] : "",
          employment_date: normalizeOptionalDateForInput(member.employment_date),
          hired_date: normalizeOptionalDateForInput(member.hired_date),
          enrollment_date: member.enrollment_date ? member.enrollment_date.split(' ')[0] : "",
          activation_date: member.activation_date ? member.activation_date.split(' ')[0] : "",
          termination_date: member.termination_date ? member.termination_date.split(' ')[0] : "",
          suspension_date: member.suspension_date ? member.suspension_date.split(' ')[0] : "",
          reactivation_date: member.reactivation_date ? member.reactivation_date.split(' ')[0] : "",
          expiry_date: member.expiry_date ? member.expiry_date.split(' ')[0] : "",
          acceptance_age: member.acceptance_age ?? "",
          termination_age: member.termination_age ?? "",
        });
      } catch (error) {
        toast.error("Failed to load member data.", { autoClose: 3000 });
        navigate("/members");
      } finally {
        setIsLoading(false);
      }
    };
    fetchMember();
  }, [memberId, navigate]);

  // Fetch corporates
  useEffect(() => {
    const fetchCorporates = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${BASE_URL}/members/corporates`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        });
        setCorporates(
          response.data.data.map((corp) => ({
            value: corp.corp_code,
            label: corp.corp_name,
          }))
        );
      } catch (error) {
        toast.error("Failed to load corporates.", { autoClose: 3000 });
      } finally {
        setIsLoading(false);
      }
    };
    fetchCorporates();
  }, []);

  // Fetch plans based on selected corporate
  useEffect(() => {
    if (formData.corp_code) {
      const fetchPlans = async () => {
        try {
          setIsLoading(true);
          const response = await axios.get(
            `${BASE_URL}/members/corporates/${formData.corp_code}/plans`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
              },
            }
          );
          setPlans(
            response.data.data.map((plan) => ({
              value: plan.plan_code,
              label: plan.plan_name,
              plan_type: plan.plan_type,
            }))
          );
        } catch (error) {
          toast.error("Failed to load plans.", { autoClose: 3000 });
        } finally {
          setIsLoading(false);
        }
      };
      fetchPlans();
    } else {
      setPlans([]);
    }
  }, [formData.corp_code]);

  // Fetch nationalities
  useEffect(() => {
    const fetchNationalities = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${BASE_URL}/members/nationalities`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        });
        setNationalities(
          response.data.data.map((nat) => ({
            value: nat.nationality,
            label: nat.nationality,
          }))
        );
      } catch (error) {
        toast.error("Failed to load nationalities.", { autoClose: 3000 });
      } finally {
        setIsLoading(false);
      }
    };
    fetchNationalities();
  }, []);

  // Fetch banks
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${BASE_URL}/members/banks`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        });
        setBanks(
          response.data.data.map((bank) => ({
            value: bank.tbl_id,
            label: bank.bank_name,
          }))
        );
      } catch (error) {
        toast.error("Failed to load banks.", { autoClose: 3000 });
      } finally {
        setIsLoading(false);
      }
    };
    fetchBanks();
  }, []);

  // Fetch regions
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        setIsLoadingAddress((prev) => ({ ...prev, region: true }));
        const response = await axios.get(`${BASE_URL}/address/get-ref-address`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        });
        setRegions(
          response.data.data.map((region) => ({
            value: region.region_code,
            label: region.region,
          }))
        );
      } catch (error) {
        toast.error("Failed to load regions.", { autoClose: 3000 });
      } finally {
        setIsLoadingAddress((prev) => ({ ...prev, region: false }));
      }
    };
    fetchRegions();
  }, []);

  // Fetch provinces
  useEffect(() => {
    if (formData.region) {
      const fetchProvinces = async () => {
        try {
          setIsLoadingAddress((prev) => ({ ...prev, province: true }));
          const response = await axios.get(`${BASE_URL}/address/get-ref-address`, {
            params: { region_code: formData.region },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
            },
          });
          setProvinces(
            response.data.data.map((province) => ({
              value: province.province_code,
              label: province.province,
            }))
          );
        } catch (error) {
          toast.error("Failed to load provinces.", { autoClose: 3000 });
        } finally {
          setIsLoadingAddress((prev) => ({ ...prev, province: false }));
        }
      };
      fetchProvinces();
    } else {
      setProvinces([]);
      setTownCities([]);
      setBarangays([]);
      setFormData((prev) => ({ ...prev, postalcode: "" }));
    }
  }, [formData.region]);

  // Fetch town/cities
  useEffect(() => {
    if (formData.province) {
      const fetchTownCities = async () => {
        try {
          setIsLoadingAddress((prev) => ({ ...prev, towncity: true }));
          const response = await axios.get(`${BASE_URL}/address/get-ref-address`, {
            params: { province_code: formData.province },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
            },
          });
          const uniqueCities = [
            ...new Map(
              response.data.data.map((city) => [city.city_code, city])
            ).values(),
          ];
          setTownCities(
            uniqueCities.map((city) => ({
              value: city.city_code,
              label: city.city,
            }))
          );
        } catch (error) {
          toast.error("Failed to load town/cities.", { autoClose: 3000 });
        } finally {
          setIsLoadingAddress((prev) => ({ ...prev, towncity: false }));
        }
      };
      fetchTownCities();
    } else {
      setTownCities([]);
      setBarangays([]);
      setFormData((prev) => ({ ...prev, postalcode: "" }));
    }
  }, [formData.province]);

  // Fetch barangays and auto-populate postal code
  useEffect(() => {
    if (formData.towncity) {
      const fetchBarangays = async () => {
        try {
          setIsLoadingAddress((prev) => ({ ...prev, barangay: true }));
          const response = await axios.get(`${BASE_URL}/address/get-ref-address`, {
            params: { city_code: formData.towncity },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
            },
          });
          const uniqueBarangays = [
            ...new Map(
              response.data.data.map((barangay) => [barangay.baranggay_code, barangay])
            ).values(),
          ];
          setBarangays(
            uniqueBarangays.map((barangay) => ({
              value: barangay.baranggay_code,
              label: barangay.baranggay,
              postalcode: barangay.postcode,
            }))
          );
        } catch (error) {
          toast.error("Failed to load barangays.", { autoClose: 3000 });
        } finally {
          setIsLoadingAddress((prev) => ({ ...prev, barangay: false }));
        }
      };
      fetchBarangays();
    } else {
      setBarangays([]);
      setFormData((prev) => ({ ...prev, postalcode: "" }));
    }
  }, [formData.towncity]);

  // Auto-populate postal code when barangay is selected
  useEffect(() => {
    if (formData.barangay) {
      const selectedBarangay = barangays.find((b) => b.value === formData.barangay);
      if (selectedBarangay && selectedBarangay.postalcode) {
        setFormData((prev) => ({ ...prev, postalcode: selectedBarangay.postalcode }));
      }
    }
  }, [formData.barangay, barangays]);

  // Fetch principals based on corporate and member type
  useEffect(() => {
    if (formData.corp_code && formData.member_type === "d") {
      const fetchPrincipals = async () => {
        try {
          setIsLoading(true);
          const response = await axios.get(
            `${BASE_URL}/members/corporates/${formData.corp_code}/principals`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
              },
            }
          );
          setPrincipals(
            response.data.data.map((principal) => ({
              value: principal.member_id,
              label: `${principal.member_id} - ${principal.member_name}`,
              member_name: principal.member_name,
            }))
          );
        } catch (error) {
          toast.error("Failed to load principals.", { autoClose: 3000 });
        } finally {
          setIsLoading(false);
        }
      };
      fetchPrincipals();
    } else {
      setPrincipals([]);
      setFormData((prev) => ({ ...prev, principal_id: "" }));
    }
  }, [formData.corp_code, formData.member_type]);

  const sanitizeMobileInput = (value) => String(value ?? "").replace(/[^0-9+\-\s]/g, "");

  const handleInputChange = (e, field) => {
    const inputValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    let normalizedValue = inputValue;
    if (field === "mob_tel" && typeof inputValue === "string") {
      normalizedValue = sanitizeMobileInput(inputValue);
    }
    if ((field === "acceptance_age" || field === "termination_age") && typeof inputValue === "string") {
      normalizedValue = sanitizeAgeInput(inputValue);
    }

    setFormData((prev) => ({ ...prev, [field]: normalizedValue }));
    setIsFormDirty(true);
  };

  const handleSelectChange = (selected, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: selected ? selected.value : "",
    }));
    setIsFormDirty(true);
  };

  const isValidMobileNumberFormat = (mobile) => {
    const rawMobile = String(mobile ?? "").trim();
    if (rawMobile === "") return false;
    if (!/^[0-9+\-\s]+$/.test(rawMobile)) return false;

    const compactMobile = rawMobile.replace(/[\s-]/g, "");
    if (compactMobile === "") return false;

    const plusMatches = compactMobile.match(/\+/g) || [];
    if (plusMatches.length > 1) return false;
    if (compactMobile.includes("+") && !compactMobile.startsWith("+")) return false;

    return (
      /^09\d{9}$/.test(compactMobile) ||
      /^9\d{9}$/.test(compactMobile) ||
      /^639\d{9}$/.test(compactMobile) ||
      /^\+639\d{9}$/.test(compactMobile)
    );
  };

  const normalizeMobileNumber = (mobile) => {
    if (!isValidMobileNumberFormat(mobile)) return null;

    const compactMobile = String(mobile ?? "").trim().replace(/[\s-]/g, "");
    if (compactMobile.startsWith("+639")) return `0${compactMobile.slice(4)}`;
    if (compactMobile.startsWith("639")) return `0${compactMobile.slice(3)}`;
    if (compactMobile.startsWith("09")) return compactMobile;
    if (compactMobile.startsWith("9")) return `0${compactMobile}`;

    return null;
  };

  const isValidEmailFormat = (email) => {
    const normalizedEmail = String(email ?? "").trim();
    if (normalizedEmail === "") {
      return false;
    }

    if (normalizedEmail.includes("..")) {
      return false;
    }

    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(normalizedEmail)) {
      return false;
    }

    const domainPart = normalizedEmail.split("@")[1] || "";
    const tld = domainPart.split(".").pop() || "";
    return /^[A-Z]{2,}$/i.test(tld);
  };

  const validateForm = () => {
    const errors = {};

    const selectedPlan = plans.find(
      (plan) => String(plan.value) === String(formData.plan_code)
    );
    const normalizedPlanType = String(selectedPlan?.plan_type ?? "")
      .trim()
      .toLowerCase();
    const normalizedMemberType = String(formData.member_type ?? "")
      .trim()
      .toLowerCase();
    const normalizedEmail = String(formData.email_add ?? "").trim();
    const normalizedPhilhealthId = String(formData.philhealth_id ?? "").trim();
    const normalizedPrincipalId = String(formData.principal_id ?? "").trim();
    const normalizedDateOfBirth = String(formData.date_of_birth ?? "").trim();
    const normalizedHiredDate = String(formData.hired_date ?? "").trim();
    const normalizedActivationDate = String(formData.activation_date ?? "").trim();
    const normalizedExpiryDate = String(formData.expiry_date ?? "").trim();
    const acceptanceAgeError = validateOptionalAge(formData.acceptance_age, "Acceptance Age");
    const terminationAgeError = validateOptionalAge(formData.termination_age, "Termination Age");

    if (!formData.corp_code) errors.corp_code = ["Corporate is required"];
    if (!formData.plan_code) errors.plan_code = ["Plan is required"];
    if (!formData.first_name) errors.first_name = ["First Name is required"];
    if (!formData.middle_name) errors.middle_name = ["Middle Name is required"];
    if (!formData.last_name) errors.last_name = ["Last Name is required"];
    if (!formData.date_of_birth) errors.date_of_birth = ["Date of Birth is required"];
    if (!formData.gender) errors.gender = ["Gender is required"];
    if (!formData.civil_status) errors.civil_status = ["Civil Status is required"];
    if (normalizedEmail && !isValidEmailFormat(normalizedEmail)) {
      errors.email_add = ["Please enter a valid email address (e.g., name@domain.com)."];
    }
    if (formData.mob_tel && !normalizeMobileNumber(formData.mob_tel)) {
      errors.mob_tel = ["Please enter a valid mobile number."];
    }
    if (!formData.member_type) errors.member_type = ["Member Type is required"];
    if (formData.member_type === "d" && !formData.principal_id) errors.principal_id = ["Principal is required"];
    if (formData.member_type === "p" && normalizedPrincipalId !== "") {
      errors.principal_id = ["Principal ID must be blank for principal member type"];
    }
    if (
      formData.member_type === "d" &&
      normalizedPrincipalId !== "" &&
      normalizedPrincipalId === String(memberId ?? "").trim()
    ) {
      errors.principal_id = ["Principal ID cannot be the same as the member being updated"];
    }
    if (
      selectedPlan &&
      normalizedPlanType !== "" &&
      normalizedPlanType !== "b" &&
      normalizedPlanType !== normalizedMemberType
    ) {
      errors.plan_code = ["Selected plan is not available for this member type"];
    }
    if (formData.philhealth_required && normalizedPhilhealthId === "") {
      errors.philhealth_id = ["PhilHealth ID is required when PhilHealth is required"];
    }
    if (acceptanceAgeError) errors.acceptance_age = [acceptanceAgeError];
    if (terminationAgeError) errors.termination_age = [terminationAgeError];
    if (!formData.activation_date) errors.activation_date = ["Activation Date is required"];
    if (!formData.expiry_date) errors.expiry_date = ["Expiry Date is required"];
    if (normalizedDateOfBirth !== "" && normalizedHiredDate !== "") {
      const dateOfBirth = new Date(`${normalizedDateOfBirth}T00:00:00`);
      const hiredDate = new Date(`${normalizedHiredDate}T00:00:00`);
      if (
        !Number.isNaN(dateOfBirth.getTime()) &&
        !Number.isNaN(hiredDate.getTime()) &&
        hiredDate < dateOfBirth
      ) {
        errors.hired_date = ["Date Hired cannot be earlier than Date of Birth"];
      }
    }
    if (normalizedActivationDate !== "" && normalizedExpiryDate !== "") {
      const activationDate = new Date(`${normalizedActivationDate}T00:00:00`);
      const expiryDate = new Date(`${normalizedExpiryDate}T00:00:00`);
      if (
        !Number.isNaN(activationDate.getTime()) &&
        !Number.isNaN(expiryDate.getTime()) &&
        activationDate >= expiryDate
      ) {
        errors.activation_date = ["Activation Date must be earlier than Expiry Date"];
      }
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fill in all required fields.", { autoClose: 5000 });
      setIsLoading(false);
      return;
    }

    const payload = { ...formData };
    delete payload.member_id;
    delete payload.employee_id;
    delete payload.member_username;
    delete payload.member_password;
    delete payload.enrollment_date;
    delete payload.termination_date;
    delete payload.suspension_date;
    delete payload.reactivation_date;
    delete payload.status_flag;
    delete payload.newfile;
    delete payload.replacefile;
    delete payload.reprintfile;

    const normalizedMobile = normalizeMobileNumber(payload.mob_tel);
    if (normalizedMobile) {
      payload.mob_tel = normalizedMobile;
    }

    payload.member_type = String(payload.member_type ?? "").trim().toLowerCase();
    if (payload.member_type === "p") {
      payload.principal_id = "";
    }
    if (String(payload.philhealth_id ?? "").trim() !== "") {
      payload.philhealth_required = true;
    }
    payload.philhealth_id =
      String(payload.philhealth_id ?? "").trim() === ""
        ? null
        : String(payload.philhealth_id).trim();

    payload.nationality =
      String(payload.nationality ?? "").trim() === ""
        ? null
        : String(payload.nationality).trim();
    payload.employment_date = normalizeOptionalDateForPayload(payload.employment_date);
    payload.hired_date = normalizeOptionalDateForPayload(payload.hired_date);
    payload.bank_id = normalizeOptionalBankIdForPayload(payload.bank_id);
    payload.acceptance_age = normalizeOptionalAgeForPayload(payload.acceptance_age);
    payload.termination_age = normalizeOptionalAgeForPayload(payload.termination_age);

    try {
      const response = await axios.put(`${BASE_URL}/members/${memberId}`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
      });
      setUpdatedMemberDetails(response.data.member);
      setShowSuccessModal(true);
      toast.success("Member updated successfully!", { autoClose: 3000 });
    } catch (error) {
      const errorMessage = error.response?.data?.message
        ? Object.values(error.response.data.message).flat().join(", ")
        : "Failed to update member.";
      setErrors(error.response?.data?.message || {});
      toast.error(errorMessage, { autoClose: 5000 });
    } finally {
    setIsLoading(false);
  }
};

  const closeModal = () => {
    setShowSuccessModal(false);
    navigate("/members");
  };

  const closeAgeInfoModal = () => {
    setShowAgeInfoModal(false);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="max-w-7xl"
      >
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Edit Member</h2>
          <button
            onClick={() => navigate("/members")}
            className="inline-flex items-center gap-2 rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 transition-colors"
            disabled={isLoading}
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back to Members
          </button>
        </div>

        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {["basic", "address", "other"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`${
                    activeTab === tab
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize transition-colors duration-200`}
                >
                  {tab === "basic" ? "Basic Details" : tab === "address" ? "Address" : "Other Details"}
                </button>
              ))}
            </nav>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white shadow-lg rounded-lg p-6">

{/* 
<div className="mt-8 flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/members")}
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium disabled:opacity-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium flex items-center space-x-2 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <ArrowPathIcon className="animate-spin h-5 w-5 text-white" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Update Member</span>
              )}
            </button>
          </div> */}


          <AnimatePresence mode="wait">
            {activeTab === "basic" && (
              <motion.div
                key="basic"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                {/* Left Column */}
                <div className="space-y-6">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Corporate <span className="text-red-500">*</span>
                    </label>
                    {isLoading ? (
                      <div className="flex items-center space-x-2 mt-1">
                        <ArrowPathIcon className="animate-spin h-5 w-5 text-gray-500" />
                        <span className="text-xs text-gray-600">Loading corporates...</span>
                      </div>
                    ) : (
                      <Select
                        options={corporates}
                        value={corporates.find((c) => c.value === formData.corp_code)}
                        onChange={(selected) => handleSelectChange(selected, "corp_code")}
                        placeholder="Select corporate"
                        isClearable
                        className="mt-1 text-sm"
                        classNamePrefix="select"
                        isDisabled={isLoading}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.corp_code ? "#ef4444" : "#e5e7eb",
                            "&:hover": { borderColor: errors.corp_code ? "#ef4444" : "#93c5fd" },
                          }),
                        }}
                      />
                    )}
                    {errors.corp_code && <p className="mt-1 text-xs text-red-500">{errors.corp_code[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Plan <span className="text-red-500">*</span>
                    </label>
                    {isLoading ? (
                      <div className="flex items-center space-x-2 mt-1">
                        <ArrowPathIcon className="animate-spin h-5 w-5 text-gray-500" />
                        <span className="text-xs text-gray-600">Loading plans...</span>
                      </div>
                    ) : (
                      <Select
                        options={plans}
                        value={plans.find((p) => p.value === formData.plan_code)}
                        onChange={(selected) => handleSelectChange(selected, "plan_code")}
                        placeholder="Select plan"
                        isClearable
                        className="mt-1 text-sm"
                        classNamePrefix="select"
                        isDisabled={isLoading || !formData.corp_code}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.plan_code ? "#ef4444" : "#e5e7eb",
                            "&:hover": { borderColor: errors.plan_code ? "#ef4444" : "#93c5fd" },
                          }),
                        }}
                      />
                    )}
                    {errors.plan_code && <p className="mt-1 text-xs text-red-500">{errors.plan_code[0]}</p>}
                    {formData.plan_code && (
                      <div className="mt-2">
                        <PlanBadge value={plans.find((p) => p.value === formData.plan_code)?.label} />
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Employee ID
                    </label>
                    <input
                      type="text"
                      value={formData.employee_id}
                      readOnly
                      aria-readonly="true"
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-600 cursor-not-allowed focus:outline-none"
                      placeholder="Employee ID"
                      disabled={isLoading}
                    />
                    {errors.employee_id && <p className="mt-1 text-xs text-red-500">{errors.employee_id[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Member ID
                      <InformationCircleIcon
                        className="inline h-4 w-4 ml-1 text-gray-400"
                        title="This field is auto-generated by the system"
                      />
                    </label>
                    <input
                      type="text"
                      value={formData.member_id || AUTO_GENERATED_PLACEHOLDER}
                      disabled
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                      placeholder={AUTO_GENERATED_PLACEHOLDER}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => handleInputChange(e, "first_name")}
                      className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        errors.first_name ? "border-red-500" : "border-gray-200"
                      }`}
                      placeholder="Enter first name"
                      disabled={isLoading}
                    />
                    {errors.first_name && <p className="mt-1 text-xs text-red-500">{errors.first_name[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Middle Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.middle_name}
                      onChange={(e) => handleInputChange(e, "middle_name")}
                      className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        errors.middle_name ? "border-red-500" : "border-gray-200"
                      }`}
                      placeholder="Enter middle name"
                      disabled={isLoading}
                    />
                    {errors.middle_name && <p className="mt-1 text-xs text-red-500">{errors.middle_name[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => handleInputChange(e, "last_name")}
                      className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        errors.last_name ? "border-red-500" : "border-gray-200"
                      }`}
                      placeholder="Enter last name"
                      disabled={isLoading}
                    />
                    {errors.last_name && <p className="mt-1 text-xs text-red-500">{errors.last_name[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Suffix</label>
                    <input
                      type="text"
                      value={formData.suffix}
                      onChange={(e) => handleInputChange(e, "suffix")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter suffix (e.g., Jr., Sr.)"
                      disabled={isLoading}
                    />
                    {errors.suffix && <p className="mt-1 text-xs text-red-500">{errors.suffix[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">VIP Tag</label>
                    <input
                      type="checkbox"
                      checked={formData.vip_tag}
                      onChange={(e) => handleInputChange({ target: { value: e.target.checked } }, "vip_tag")}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-200 rounded"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                {/* Right Column */}
                <div className="space-y-6">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Nickname</label>
                    <input
                      type="text"
                      value={formData.nickname}
                      onChange={(e) => handleInputChange(e, "nickname")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter nickname"
                      disabled={isLoading}
                    />
                    {errors.nickname && <p className="mt-1 text-xs text-red-500">{errors.nickname[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => handleInputChange(e, "date_of_birth")}
                      draplaceholder="Enter date of birth"
                      disabled={isLoading}
                    />
                    {errors.date_of_birth && <p className="mt-1 text-xs text-red-500">{errors.date_of_birth[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Passport Number</label>
                    <input
                      type="text"
                      value={formData.passport_no}
                      onChange={(e) => handleInputChange(e, "passport_no")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter passport number"
                      disabled={isLoading}
                    />
                    {errors.passport_no && <p className="mt-1 text-xs text-red-500">{errors.passport_no[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Policy Number</label>
                    <input
                      type="text"
                      value={formData.policy_no}
                      onChange={(e) => handleInputChange(e, "policy_no")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter policy number"
                      disabled={isLoading}
                    />
                    {errors.policy_no && <p className="mt-1 text-xs text-red-500">{errors.policy_no[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Bank Name</label>
                    {isLoading ? (
                      <div className="flex items-center space-x-2 mt-1">
                        <ArrowPathIcon className="animate-spin h-5 w-5 text-gray-500" />
                        <span className="text-xs text-gray-600">Loading banks...</span>
                      </div>
                    ) : (
                      <Select
                        options={banks}
                        value={getSelectedBankOption(formData.bank_id)}
                        onChange={(selected) => handleSelectChange(selected, "bank_id")}
                        placeholder="Select bank"
                        isClearable
                        className="mt-1 text-sm"
                        classNamePrefix="select"
                        isDisabled={isLoading}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.bank_id ? "#ef4444" : "#e5e7eb",
                            "&:hover": { borderColor: errors.bank_id ? "#ef4444" : "#93c5fd" },
                          }),
                        }}
                      />
                    )}
                    {errors.bank_id && <p className="mt-1 text-xs text-red-500">{errors.bank_id[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Bank Branch</label>
                    <input
                      type="text"
                      value={formData.bank_branch}
                      onChange={(e) => handleInputChange(e, "bank_branch")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter bank branch"
                      disabled={isLoading}
                    />
                    {errors.bank_branch && <p className="mt-1 text-xs text-red-500">{errors.bank_branch[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Bank Account Number</label>
                    <input
                      type="text"
                      value={formData.bank_account_no}
                      onChange={(e) => handleInputChange(e, "bank_account_no")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter bank account number"
                      disabled={isLoading}
                    />
                    {errors.bank_account_no && (
                      <p className="mt-1 text-xs text-red-500">{errors.bank_account_no[0]}</p>
                    )}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Bank Account Name</label>
                    <input
                      type="text"
                      value={formData.bank_acct_name}
                      onChange={(e) => handleInputChange(e, "bank_acct_name")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter bank account name"
                      disabled={isLoading}
                    />
                    {errors.bank_acct_name && <p className="mt-1 text-xs text-red-500">{errors.bank_acct_name[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={genderOptions}
                      value={getSelectedOption(genderOptions, formData.gender, {
                        male: "m",
                        female: "f",
                      })}
                      onChange={(selected) => handleSelectChange(selected, "gender")}
                      placeholder="Select gender"
                      isClearable
                      className="mt-1 text-sm"
                      classNamePrefix="select"
                      isDisabled={isLoading}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: errors.gender ? "#ef4444" : "#e5e7eb",
                          "&:hover": { borderColor: errors.gender ? "#ef4444" : "#93c5fd" },
                        }),
                      }}
                    />
                    {errors.gender && <p className="mt-1 text-xs text-red-500">{errors.gender[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Civil Status <span className="text-red-500">*</span>
                    </label>
                    {/* <option value="">Select civil status</option>
                      <option value="s">Single</option>
                      <option value="m">Married</option>
                      <option value="p">Separated</option>
                      <option value="w">Widowed</option>
                      <option value="d">Divorced</option> */}
                    <Select
                      options={civilStatusOptions}
                      value={getSelectedOption(civilStatusOptions, formData.civil_status, {
                        single: "s",
                        married: "m",
                        separated: "p",
                        widowed: "w",
                        divorced: "d",
                      })}
                      onChange={(selected) => handleSelectChange(selected, "civil_status")}
                      placeholder="Select civil status"
                      isClearable
                      className="mt-1 text-sm"
                      classNamePrefix="select"
                      isDisabled={isLoading}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: errors.civil_status ? "#ef4444" : "#e5e7eb",
                          "&:hover": { borderColor: errors.civil_status ? "#ef4444" : "#93c5fd" },
                        }),
                      }}
                    />
                    {errors.civil_status && (
                      <p className="mt-1 text-xs text-red-500">{errors.civil_status[0]}</p>
                    )}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Nationality</label>
                    {isLoading ? (
                      <div className="flex items-center space-x-2 mt-1">
                        <ArrowPathIcon className="animate-spin h-5 w-5 text-gray-500" />
                        <span className="text-xs text-gray-600">Loading nationalities...</span>
                      </div>
                    ) : (
                      <Select
                        options={nationalities}
                        value={nationalities.find((n) => n.value === formData.nationality)}
                        onChange={(selected) => handleSelectChange(selected, "nationality")}
                        placeholder="Select nationality"
                        isClearable
                        className="mt-1 text-sm"
                        classNamePrefix="select"
                        isDisabled={isLoading}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.nationality ? "#ef4444" : "#e5e7eb",
                            "&:hover": { borderColor: errors.nationality ? "#ef4444" : "#93c5fd" },
                          }),
                        }}
                      />
                    )}
                    {errors.nationality && <p className="mt-1 text-xs text-red-500">{errors.nationality[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                      <span>Acceptance Age</span>
                      <button
                        type="button"
                        onClick={() => setShowAgeInfoModal(true)}
                        className="text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
                        aria-label="Acceptance and termination age information"
                      >
                        <InformationCircleIcon className="h-4 w-4" />
                      </button>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={3}
                      value={formData.acceptance_age}
                      onChange={(e) => handleInputChange(e, "acceptance_age")}
                      className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        errors.acceptance_age ? "border-red-500" : "border-gray-200"
                      }`}
                      placeholder="Enter acceptance age"
                      disabled={isLoading}
                    />
                    {errors.acceptance_age && (
                      <p className="mt-1 text-xs text-red-500">{errors.acceptance_age[0]}</p>
                    )}
                  </div>
                  <div className="relative">
                    <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                      <span>Termination Age</span>
                      <button
                        type="button"
                        onClick={() => setShowAgeInfoModal(true)}
                        className="text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
                        aria-label="Acceptance and termination age information"
                      >
                        <InformationCircleIcon className="h-4 w-4" />
                      </button>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={3}
                      value={formData.termination_age}
                      onChange={(e) => handleInputChange(e, "termination_age")}
                      className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        errors.termination_age ? "border-red-500" : "border-gray-200"
                      }`}
                      placeholder="Enter termination age"
                      disabled={isLoading}
                    />
                    {errors.termination_age && (
                      <p className="mt-1 text-xs text-red-500">{errors.termination_age[0]}</p>
                    )}
                  </div>
                </div>
                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Special Instructions</label>
                    <textarea
                      value={formData.special_instructions}
                      onChange={(e) => handleInputChange(e, "special_instructions")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter special instructions"
                      rows="4"
                      disabled={isLoading}
                    />
                    {errors.special_instructions && (
                      <p className="mt-1 text-xs text-red-500">{errors.special_instructions[0]}</p>
                    )}
                  </div>
              </motion.div>
            )}
            {activeTab === "address" && (
              <motion.div
                key="address"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <div className="space-y-6">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Street/Building No.</label>
                    <input
                      type="text"
                      value={formData.streetno_bldg}
                      onChange={(e) => handleInputChange(e, "streetno_bldg")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter street/building number"
                      disabled={isLoading}
                    />
                    {errors.streetno_bldg && <p className="mt-1 text-xs text-red-500">{errors.streetno_bldg[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Region</label>
                    {isLoadingAddress.region ? (
                      <div className="flex items-center space-x-2 mt-1">
                        <ArrowPathIcon className="animate-spin h-5 w-5 text-gray-500" />
                        <span className="text-xs text-gray-600">Loading regions...</span>
                      </div>
                    ) : (
                      <Select
                        options={regions}
                        value={regions.find((r) => r.value === formData.region)}
                        onChange={(selected) => {
                          handleSelectChange(selected, "region");
                          setFormData((prev) => ({
                            ...prev,
                            province: "",
                            towncity: "",
                            barangay: "",
                            postalcode: "",
                          }));
                        }}
                        placeholder="Select region"
                        isClearable
                        className="mt-1 text-sm"
                        classNamePrefix="select"
                        isDisabled={isLoading}
                      />
                    )}
                    {errors.region && <p className="mt-1 text-xs text-red-500">{errors.region[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Province</label>
                    {isLoadingAddress.province ? (
                      <div className="flex items-center space-x-2 mt-1">
                        <ArrowPathIcon className="animate-spin h-5 w-5 text-gray-500" />
                        <span className="text-xs text-gray-600">Loading provinces...</span>
                      </div>
                    ) : (
                      <Select
                        options={provinces}
                        value={provinces.find((p) => p.value === formData.province)}
                        onChange={(selected) => {
                          handleSelectChange(selected, "province");
                          setFormData((prev) => ({
                            ...prev,
                            towncity: "",
                            barangay: "",
                            postalcode: "",
                          }));
                        }}
                        placeholder="Select province"
                        isClearable
                        className="mt-1 text-sm"
                        classNamePrefix="select"
                        isDisabled={isLoading || !formData.region}
                      />
                    )}
                    {errors.province && <p className="mt-1 text-xs text-red-500">{errors.province[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Town/City</label>
                    {isLoadingAddress.towncity ? (
                      <div className="flex items-center space-x-2 mt-1">
                        <ArrowPathIcon className="animate-spin h-5 w-5 text-gray-500" />
                        <span className="text-xs text-gray-600">Loading towns/cities...</span>
                      </div>
                    ) : (
                      <Select
                        options={townCities}
                        value={townCities.find((t) => t.value === formData.towncity)}
                        onChange={(selected) => {
                          handleSelectChange(selected, "towncity");
                          setFormData((prev) => ({
                            ...prev,
                            barangay: "",
                            postalcode: "",
                          }));
                        }}
                        placeholder="Select town/city"
                        isClearable
                        className="mt-1 text-sm"
                        classNamePrefix="select"
                        isDisabled={isLoading || !formData.province}
                      />
                    )}
                    {errors.towncity && <p className="mt-1 text-xs text-red-500">{errors.towncity[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Barangay</label>
                    {isLoadingAddress.barangay ? (
                      <div className="flex items-center space-x-2 mt-1">
                        <ArrowPathIcon className="animate-spin h-5 w-5 text-gray-500" />
                        <span className="text-xs text-gray-600">Loading barangays...</span>
                      </div>
                    ) : (
                      <Select
                        options={barangays}
                        value={barangays.find((b) => b.value === formData.barangay)}
                        onChange={(selected) => handleSelectChange(selected, "barangay")}
                        placeholder="Select barangay"
                        isClearable
                        className="mt-1 text-sm"
                        classNamePrefix="select"
                        isDisabled={isLoading || !formData.towncity}
                      />
                    )}
                    {errors.barangay && <p className="mt-1 text-xs text-red-500">{errors.barangay[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                    <input
                      type="text"
                      value={formData.postalcode}
                      onChange={(e) => handleInputChange(e, "postalcode")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter postal code"
                      disabled={isLoading || formData.barangay}
                    />
                    {errors.postalcode && <p className="mt-1 text-xs text-red-500">{errors.postalcode[0]}</p>}
                  </div>
                </div>
              </motion.div>
            )}
            {activeTab === "other" && (
              <motion.div
                key="other"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                {/* Left Column */}
                <div className="space-y-6">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={formData.email_add}
                      onChange={(e) => handleInputChange(e, "email_add")}
                      className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        errors.email_add ? "border-red-500" : "border-gray-200"
                      }`}
                      placeholder="Enter email address"
                      disabled={isLoading}
                    />
                    {errors.email_add && <p className="mt-1 text-xs text-red-500">{errors.email_add[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Telephone Number</label>
                    <input
                      type="text"
                      value={formData.tel_no}
                      onChange={(e) => handleInputChange(e, "tel_no")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter telephone number"
                      disabled={isLoading}
                    />
                    {errors.tel_no && <p className="mt-1 text-xs text-red-500">{errors.tel_no[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Mobile Number
                    </label>
                    <input
                      type="text"
                      value={formData.mob_tel}
                      onChange={(e) => handleInputChange(e, "mob_tel")}
                      className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        errors.mob_tel ? "border-red-500" : "border-gray-200"
                      }`}
                      placeholder="Enter mobile number"
                      inputMode="tel"
                      maxLength={20}
                      disabled={isLoading}
                    />
                    {errors.mob_tel && <p className="mt-1 text-xs text-red-500">{errors.mob_tel[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">SSS Number</label>
                    <input
                      type="text"
                      value={formData.sss_no}
                      onChange={(e) => handleInputChange(e, "sss_no")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter SSS number"
                      disabled={isLoading}
                    />
                    {errors.sss_no && <p className="mt-1 text-xs text-red-500">{errors.sss_no[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">PhilHealth Required</label>
                    <input
                      type="checkbox"
                      checked={formData.philhealth_required}
                      onChange={(e) => handleInputChange({ target: { value: e.target.checked } }, "philhealth_required")}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-200 rounded"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">PhilHealth Number</label>
                    <input
                      type="text"
                      value={formData.philhealth_id}
                      onChange={(e) => handleInputChange(e, "philhealth_id")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter PhilHealth number"
                      disabled={isLoading}
                    />
                    {errors.philhealth_id && <p className="mt-1 text-xs text-red-500">{errors.philhealth_id[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">TIN Number</label>
                    <input
                      type="text"
                      value={formData.tin_no}
                      onChange={(e) => handleInputChange(e, "tin_no")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter TIN number"
                      disabled={isLoading}
                    />
                    {errors.tin_no && <p className="mt-1 text-xs text-red-500">{errors.tin_no[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Position</label>
                    <input
                      type="text"
                      value={formData.position}
                      onChange={(e) => handleInputChange(e, "position")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter position"
                      disabled={isLoading}
                    />
                    {errors.position && <p className="mt-1 text-xs text-red-500">{errors.position[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Designation</label>
                    <input
                      type="text"
                      value={formData.designation}
                      onChange={(e) => handleInputChange(e, "designation")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter designation"
                      disabled={isLoading}
                    />
                    {errors.designation && <p className="mt-1 text-xs text-red-500">{errors.designation[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Employment Date</label>
                    <input
                      type="date"
                      value={normalizeOptionalDateForInput(formData.employment_date)}
                      onChange={(e) => handleInputChange(e, "employment_date")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      disabled={isLoading}
                    />
                    {errors.employment_date && (
                      <p className="mt-1 text-xs text-red-500">{errors.employment_date[0]}</p>
                    )}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Department</label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => handleInputChange(e, "department")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter department"
                      disabled={isLoading}
                    />
                    {errors.department && <p className="mt-1 text-xs text-red-500">{errors.department[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Member Type <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1 flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="member_type"
                          value="p"
                          checked={formData.member_type === "p"}
                          onChange={(e) => handleSelectChange({ value: "p" }, "member_type")}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-200"
                          disabled={isLoading}
                        />
                        <span className="ml-2 text-sm text-gray-700">Principal</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="member_type"
                          value="d"
                          checked={formData.member_type === "d"}
                          onChange={(e) => handleSelectChange({ value: "d" }, "member_type")}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-200"
                          disabled={isLoading}
                        />
                        <span className="ml-2 text-sm text-gray-700">Dependent</span>
                      </label>
                    </div>
                    {errors.member_type && <p className="mt-1 text-xs text-red-500">{errors.member_type[0]}</p>}
                  </div>
                  {formData.member_type === "d" && (
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700">
                        Principal <span className="text-red-500">*</span>
                      </label>
                      {isLoading ? (
                        <div className="flex items-center space-x-2 mt-1">
                          <ArrowPathIcon className="animate-spin h-5 w-5 text-gray-500" />
                          <span className="text-xs text-gray-600">Loading principals...</span>
                        </div>
                      ) : (
                        <Select
                          options={principals}
                          value={principals.find((p) => p.value === formData.principal_id)}
                          onChange={(selected) => handleSelectChange(selected, "principal_id")}
                          placeholder="Select principal"
                          isClearable
                          className="mt-1 text-sm"
                          classNamePrefix="select"
                          isDisabled={isLoading || !formData.corp_code}
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderColor: errors.principal_id ? "#ef4444" : "#e5e7eb",
                              "&:hover": { borderColor: errors.principal_id ? "#ef4444" : "#93c5fd" },
                            }),
                          }}
                        />
                      )}
                      {errors.principal_id && <p className="mt-1 text-xs text-red-500">{errors.principal_id[0]}</p>}
                      {formData.principal_id && (
                        <div className="mt-2 bg-blue-100 p-3 rounded-md">
                          <h3 className="text-sm font-medium text-gray-700">Your Principal Details</h3>
                          <p className="text-xs text-gray-600">Member ID: {formData.principal_id}</p>
                          <p className="text-xs text-gray-600">
                            Name: {principals.find((p) => p.value === formData.principal_id)?.member_name}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Right Column */}
                <div className="space-y-6">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Date Hired</label>
                    <input
                      type="date"
                      value={normalizeOptionalDateForInput(formData.hired_date)}
                      onChange={(e) => handleInputChange(e, "hired_date")}
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      disabled={isLoading}
                    />
                    {errors.hired_date && <p className="mt-1 text-xs text-red-500">{errors.hired_date[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Date Enrolled
                      <InformationCircleIcon
                        className="inline h-4 w-4 ml-1 text-gray-400"
                        title="This field is auto-generated by the system"
                      />
                    </label>
                    <input
                      type="text"
                      value={formData.enrollment_date || AUTO_GENERATED_PLACEHOLDER}
                      disabled
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                      placeholder={AUTO_GENERATED_PLACEHOLDER}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Activation Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.activation_date}
                      onChange={(e) => handleInputChange(e, "activation_date")}
                      max={formData.expiry_date || undefined}
                      className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        errors.activation_date ? "border-red-500" : "border-gray-200"
                      }`}
                      disabled={isLoading}
                    />
                    {errors.activation_date && (
                      <p className="mt-1 text-xs text-red-500">{errors.activation_date[0]}</p>
                    )}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Termination Date
                      <InformationCircleIcon
                        className="inline h-4 w-4 ml-1 text-gray-400"
                        title="This field is auto-generated by the system"
                      />
                    </label>
                    <input
                      type="text"
                      value={formData.termination_date || AUTO_GENERATED_PLACEHOLDER}
                      disabled
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                      placeholder={AUTO_GENERATED_PLACEHOLDER}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Suspension Date
                      <InformationCircleIcon
                        className="inline h-4 w-4 ml-1 text-gray-400"
                        title="This field is auto-generated by the system"
                      />
                    </label>
                    <input
                      type="text"
                      value={formData.suspension_date || AUTO_GENERATED_PLACEHOLDER}
                      disabled
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                      placeholder={AUTO_GENERATED_PLACEHOLDER}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Reactivation Date
                      <InformationCircleIcon
                        className="inline h-4 w-4 ml-1 text-gray-400"
                        title="This field is auto-generated by the system"
                      />
                    </label>
                    <input
                      type="text"
                      value={formData.reactivation_date || AUTO_GENERATED_PLACEHOLDER}
                      disabled
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                      placeholder={AUTO_GENERATED_PLACEHOLDER}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Expiry Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.expiry_date}
                      onChange={(e) => handleInputChange(e, "expiry_date")}
                      min={formData.activation_date || undefined}
                      className={`mt-1 block w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                        errors.expiry_date ? "border-red-500" : "border-gray-200"
                      }`}
                      disabled={isLoading}
                    />
                    {errors.expiry_date && <p className="mt-1 text-xs text-red-500">{errors.expiry_date[0]}</p>}
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                      <InformationCircleIcon
                        className="inline h-4 w-4 ml-1 text-gray-400"
                        title="This field is auto-generated by the system"
                      />
                    </label>
                    <input
                      type="text"
                      value={formData.status_flag === "1" ? "Active" : "Inactive"}
                      disabled
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                      placeholder={AUTO_GENERATED_PLACEHOLDER}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      New File
                      <InformationCircleIcon
                        className="inline h-4 w-4 ml-1 text-gray-400"
                        title="This field is auto-generated by the system"
                      />
                    </label>
                    <input
                      type="text"
                      value={formData.newfile || AUTO_GENERATED_PLACEHOLDER}
                      disabled
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                      placeholder={AUTO_GENERATED_PLACEHOLDER}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Replace File
                      <InformationCircleIcon
                        className="inline h-4 w-4 ml-1 text-gray-400"
                        title="This field is auto-generated by the system"
                      />
                    </label>
                    <input
                      type="text"
                      value={formData.replacefile || AUTO_GENERATED_PLACEHOLDER}
                      disabled
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                      placeholder={AUTO_GENERATED_PLACEHOLDER}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Reprint File
                      <InformationCircleIcon
                        className="inline h-4 w-4 ml-1 text-gray-400"
                        title="This field is auto-generated by the system"
                      />
                    </label>
                    <input
                      type="text"
                      value={formData.reprintfile || AUTO_GENERATED_PLACEHOLDER}
                      disabled
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                      placeholder={AUTO_GENERATED_PLACEHOLDER}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Username
                      <InformationCircleIcon
                        className="inline h-4 w-4 ml-1 text-gray-400"
                        title="This field is auto-generated by the system"
                      />
                    </label>
                    <input
                      type="text"
                      value={formData.member_username || AUTO_GENERATED_PLACEHOLDER}
                      disabled
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                      placeholder={AUTO_GENERATED_PLACEHOLDER}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">
                      Password
                      <InformationCircleIcon
                        className="inline h-4 w-4 ml-1 text-gray-400"
                        title="This field is auto-generated based on date of birth (MMDDYY)"
                      />
                    </label>
                    <input
                      type="text"
                      value={AUTO_GENERATED_PLACEHOLDER}
                      disabled
                      className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                      placeholder={AUTO_GENERATED_PLACEHOLDER}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-8 flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/members")}
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium disabled:opacity-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium flex items-center space-x-2 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <ArrowPathIcon className="animate-spin h-5 w-5 text-white" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Update Member</span>
              )}
            </button>
          </div>

        </form>
      </motion.div>

      {/* Age Override Info Modal */}
      <AnimatePresence>
        {showAgeInfoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={closeAgeInfoModal}
            role="dialog"
            aria-labelledby="age-info-modal-title"
            aria-modal="true"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 id="age-info-modal-title" className="text-lg font-semibold text-gray-900">
                  Acceptance and Termination Age
                </h3>
                <button
                  type="button"
                  onClick={closeAgeInfoModal}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
                  aria-label="Close age information modal"
                >
                  <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-700">
                Acceptance age and termination age are normally set from the corporate profile.
                You can override them for this member by entering values in the Acceptance Age
                and Termination Age fields.
              </p>
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={closeAgeInfoModal}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccessModal && updatedMemberDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
            onClick={closeModal}
            role="dialog"
            aria-labelledby="modal-title"
            aria-modal="true"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="bg-white rounded-2xl p-6 sm:p-8 max-w-lg w-full mx-4 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 id="modal-title" className="text-2xl font-bold text-gray-900">
                  Member has been successfully updated
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
                  aria-label="Close modal"
                >
                  <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-green-50 to-green-100 p-5 rounded-xl">
                  <p className="text-sm font-semibold text-green-800 mb-3">Member Details</p>
                  <div className="space-y-3">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Member ID:</span> {updatedMemberDetails.member_id}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Name:</span>{" "}
                      {updatedMemberDetails.first_name} {updatedMemberDetails.middle_name}{" "}
                      {updatedMemberDetails.last_name}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Corporate:</span> {updatedMemberDetails.corp_name}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Plan:</span>{" "}
                      <PlanBadge value={updatedMemberDetails.plan_name} />
                    </p>
                    {/* <p className="text-sm text-gray-700">
                      <span className="font-medium">Status:</span>{" "}
                      {updatedMemberDetails.status_flag === "1" ? "Active" : "Inactive"}
                    </p> */}
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={closeModal}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-2.5 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Go to Members List
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EditMemberForm;
